// The field capture app's frame and landing page. Both are in the main bundle: an installed
// phone opens straight onto `/capture`. The project, album and queue screens load on demand,
// and are fetched once the frame is up so the service worker has them for a site with no signal.

import {
  Link,
  Outlet,
  useCanGoBack,
  useParams,
  useRouteContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { ChevronLeft, CloudUpload, ShieldOff, WifiOff } from "lucide-react";
import { useEffect } from "react";
import { InstallHint } from "@/components/capture/InstallHint";
import { LocationNotice } from "@/components/capture/LocationNotice";
import { ProjectList } from "@/components/capture/ProjectList";
import { QueueBanner } from "@/components/capture/QueueBanner";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { CaptureLocationProvider, useCaptureProjects, useOnline } from "@/hooks/use-capture";
import { useUploadQueue } from "@/hooks/use-upload-queue";
import { hasPmAccess } from "@/lib/access";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

function preloadScreens() {
  Promise.all([
    import("@/routes/capture-project"),
    import("@/routes/capture-album"),
    import("@/routes/capture-queue"),
  ]).catch((error: unknown) => {
    logger.warn("capture_preload_failed", { error: String(error) });
  });
}

function QueueChip() {
  const { summary } = useUploadQueue();
  const waiting = summary.waiting + summary.sending;
  const label =
    waiting > 0 ? `${waiting} waiting` : summary.failed > 0 ? `${summary.failed} failed` : null;
  return (
    <Link
      to="/capture/queue"
      aria-label={label ? undefined : "Upload queue"}
      data-testid="queue-chip"
      className={cn(
        "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm font-medium tabular-nums transition-colors hover:bg-muted",
        waiting > 0 && "border-[var(--brand-accent)] text-[var(--brand-accent)] dark:text-blue-300",
        waiting === 0 && summary.failed > 0 && "border-destructive text-destructive",
      )}
    >
      <CloudUpload className="size-4" />
      {label ?? "Queue"}
    </Link>
  );
}

/** Where the header's back arrow goes, and what the screen is called. */
function useScreen(allowed: boolean) {
  const { projectId, albumId } = useParams({ strict: false });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data } = useCaptureProjects(allowed);
  const project = projectId ? data?.find((p) => p.id === projectId) : undefined;
  const album = albumId ? project?.albums.find((a) => a.id === albumId) : undefined;

  if (pathname.startsWith("/capture/queue")) {
    return { kind: "queue", title: "Upload queue", subtitle: null } as const;
  }
  if (projectId && albumId) {
    return {
      kind: "album",
      title: album?.name ?? "Album",
      subtitle: project?.name ?? null,
      projectId,
    } as const;
  }
  if (projectId) {
    return {
      kind: "project",
      title: project?.name ?? "Project",
      subtitle: project?.spNumber ?? null,
    } as const;
  }
  return { kind: "home", title: "Take photos", subtitle: null } as const;
}

const backClass =
  "-ml-1 inline-flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-muted";

function BackButton({ screen }: { screen: ReturnType<typeof useScreen> }) {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const icon = <ChevronLeft className="size-6" />;
  switch (screen.kind) {
    case "home":
      return (
        <Link to="/dashboard" aria-label="Back to the portal" className={backClass}>
          {icon}
        </Link>
      );
    case "project":
      return (
        <Link to="/capture" aria-label="Back to projects" className={backClass}>
          {icon}
        </Link>
      );
    case "album":
      return (
        <Link
          to="/capture/projects/$projectId"
          params={{ projectId: screen.projectId }}
          aria-label="Back to albums"
          className={backClass}
        >
          {icon}
        </Link>
      );
    case "queue":
      return canGoBack ? (
        <button
          type="button"
          aria-label="Back"
          onClick={() => router.history.back()}
          className={backClass}
        >
          {icon}
        </button>
      ) : (
        <Link to="/capture" aria-label="Back to projects" className={backClass}>
          {icon}
        </Link>
      );
  }
}

function CaptureHeader({ allowed }: { allowed: boolean }) {
  const screen = useScreen(allowed);
  const online = useOnline();
  return (
    <header className="sticky top-0 z-30 border-b bg-background/95 pt-[env(safe-area-inset-top)] backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto flex min-h-14 max-w-2xl items-center gap-2 py-2 pr-[max(env(safe-area-inset-right),1rem)] pl-[max(env(safe-area-inset-left),0.75rem)]">
        <BackButton screen={screen} />
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-lg leading-tight font-bold">{screen.title}</h1>
          {screen.subtitle && (
            <p className="truncate text-xs text-muted-foreground">{screen.subtitle}</p>
          )}
        </div>
        {allowed && screen.kind !== "queue" && <QueueChip />}
      </div>
      {!online && (
        <p
          role="status"
          className="flex items-center gap-2 border-t bg-muted px-4 py-2 text-sm text-muted-foreground"
        >
          <WifiOff className="size-4 shrink-0" />
          You are offline. Photos stay on this phone and upload when there is signal.
        </p>
      )}
    </header>
  );
}

const mainClass =
  "mx-auto max-w-2xl pt-4 pr-[max(env(safe-area-inset-right),1rem)] pb-[calc(env(safe-area-inset-bottom)+7rem)] pl-[max(env(safe-area-inset-left),1rem)]";

/** The capture frame: a phone header with back, title and the queue chip. No sidebar. */
export function CaptureLayout() {
  const { user } = useRouteContext({ from: "/capture" });
  const allowed = hasPmAccess({ user });

  useEffect(() => {
    if (allowed) preloadScreens();
  }, [allowed]);

  if (!allowed) {
    return (
      <div className="min-h-dvh bg-background">
        <CaptureHeader allowed={false} />
        <main className={mainClass}>
          <EmptyState
            icon={ShieldOff}
            title="Taking photos is for Madison project managers."
            description="You can see the photos shared with your firm in the portal."
            action={
              <Button asChild variant="outline">
                <Link to="/dashboard">Open the portal</Link>
              </Button>
            }
            className="rounded-lg border border-dashed py-16"
          />
        </main>
      </div>
    );
  }

  return (
    <CaptureLocationProvider>
      <div className="min-h-dvh bg-background">
        <CaptureHeader allowed />
        <main className={mainClass}>
          <Outlet />
        </main>
      </div>
    </CaptureLocationProvider>
  );
}

export function CaptureHomePage() {
  return (
    <div className="space-y-3">
      <LocationNotice />
      <QueueBanner />
      <InstallHint />
      <h2 className="pt-2 text-sm font-semibold text-muted-foreground">Projects</h2>
      <ProjectList />
    </div>
  );
}
